import React, { useState, useEffect, useRef } from "react";
// import { gl_start } from "webgl.js";

// Mock gl_start function since we don't have the actual implementation
function gl_start(canvas, scene) {
  if (!canvas) return;
  
  const gl = canvas.getContext('webgl2');
  if (!gl) {
    console.error('WebGL2 not supported');
    return;
  }

  // Create and compile shader
  function createShader(gl, type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('Shader compile error:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  // Create shader program
  function createProgram(gl, vertexShader, fragmentShader) {
    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      return null;
    }
    return program;
  }

  const vertexShader = createShader(gl, gl.VERTEX_SHADER, scene.vertexShader);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, scene.fragmentShader);
  const program = createProgram(gl, vertexShader, fragmentShader);

  // Create buffer and load vertex data
  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  const vertices = new Float32Array(scene.vertices.flat());
  gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

  // Set up vertex attribute
  const positionLocation = gl.getAttribLocation(program, 'aPos');
  gl.enableVertexAttribArray(positionLocation);
  gl.vertexAttribPointer(positionLocation, 3, gl.FLOAT, false, 0, 0);

  // Clear and draw
  gl.viewport(0, 0, canvas.width, canvas.height);
  gl.clearColor(0.0, 0.0, 0.0, 1.0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(program);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
}

export default function CG0() {
  const canvasRef = useRef(null);
  const [scene, setScene] = useState(null);
  const [hoveredTriangle, setHoveredTriangle] = useState(null);

  useEffect(() => {
    function Scene() {
      this.vertices = [
        [-1, 1, 0], [1, 1, 0], [-1, -1, 0],
        [1, -1, 0], [-1, -1, 0], [1, 1, 0],
      ];

      this.vertexShader = `#version 300 es
      in vec3 aPos;
      out vec3 vColor;
      void main() {
          gl_Position = vec4(aPos, 1.0);
          vColor = vec3(
              0.5 + 0.5 * aPos.x,
              0.5 + 0.5 * aPos.y,
              0.5 - 0.5 * aPos.x
          );
      }`;

      this.fragmentShader = `#version 300 es
      precision highp float;
      in vec3 vColor;
      out vec4 fragColor;
      void main() {
          fragColor = vec4(vColor, 1.0);
      }`;
    }

    const newScene = new Scene();
    gl_start(canvasRef.current, newScene);
    setScene(newScene);
  }, []);

  const width = 640;
  const height = 640;
  const clipToCanvas = ([x, y]) => [((x + 1) / 2) * width, ((1 - y) / 2) * height];

  return (
    <div>
        <div style={{ position: "relative", width, height, border: '1px solid #ccc' }}>
            <canvas
            ref={canvasRef}
            width={width}
            height={height}
            style={{ position: "absolute", top: 0, left: 0, zIndex: 1 }}
            />

            {/* SVG overlay for hover detection */}
            <svg
            width={width}
            height={height}
            style={{ position: "absolute", top: 0, left: 0, zIndex: 2, pointerEvents: 'auto' }}
            >
            {scene && [0, 3].map((offset) => {
                const triangleIndex = offset === 0 ? 0 : 1;
                const points = scene.vertices
                .slice(offset, offset + 3)
                .map(clipToCanvas)
                .map(([cx, cy]) => `${cx},${cy}`)
                .join(" ");

                return (
                <polygon
                    key={offset}
                    points={points}
                    fill={hoveredTriangle === triangleIndex ? "white" : "transparent"}
                    style={{ 
                        cursor: "pointer",
                        animation: hoveredTriangle === triangleIndex ? "oscillate 2s infinite" : "none"
                    }}
                    // stroke = { triangleIndex === 0 ? "red" : "blue" }
                    stroke = "white"
                    strokeWidth = "1"
                    strokeOpacity = {hoveredTriangle === triangleIndex ? "1" : "0.1"}
                    onMouseEnter={() => setHoveredTriangle(triangleIndex)}
                    onMouseLeave={() => setHoveredTriangle(null)}
                />
                );
            })}
            </svg>

            {/* Vertex labels */}
            {scene &&
            [0, 3].map((offset) => {
                const triangleIndex = offset === 0 ? 0 : 1;
                if (hoveredTriangle !== triangleIndex) return null;
                return scene.vertices.slice(offset, offset + 3).map(([x, y], i) => {
                const [cx, cy] = clipToCanvas([x, y]);
                
                // Calculate color values using the shader logic
                const colorR = (0.5 + 0.5 * x);
                const colorG = (0.5 + 0.5 * y);
                const colorB = (0.5 - 0.5 * x);
                
                return (
                    <div key = { i }>
                        <div
                        style={{
                            position: "absolute",
                            left: cx,
                            top: cy,
                            transform: "translate(-50%, -120%)",
                            background: triangleIndex === 0 ? "orange" : "skyblue",
                            pointerEvents: "none",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            textAlign: "left",
                            zIndex: 10,
                            whiteSpace: "nowrap",
                        }}
                        >
                            <p>V{i + offset + 1}</p>
                            <p>pos: ({x}, {y})</p>

                            <div style = { { display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" } }>
                                <p>color: ({colorR}, {colorG}, {colorB})</p>
                                <div style={{ 
                                    width: "10px", 
                                    height: "10px", 
                                    background: `rgb(${colorR * 255}, ${colorG * 255}, ${colorB * 255})`,
                                    borderRadius: "2px"
                                }}></div>
                            </div>

                        </div>

                        <div
                            style={{
                                position: "absolute",
                                left: cx,
                                top: cy,
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                background: triangleIndex === 0 ? "orange" : "skyblue",
                                transform: "translate(-50%, -50%)",
                                zIndex: 9,
                                pointerEvents: "none",
                                border: "2px solid white",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.3)"
                            }}
                            />
                    </div>
                );
                });
            })}
        </div>
    </div>
  );
}